/**
 * Sequencer Service
 * Handles sequencer generation and preview requests
 */

import { getApiClient } from '../client';

/**
 * Sequencer slot configuration (backend format with snake_case)
 */
export interface SequencerSlot {
  type: 'crate' | 'stopset';
  playlist_id: string | null;
  mode: 'OPTIMIZE' | 'FORCE_NEXT';
  repeat_count?: number;
}

/**
 * Request to generate a sequence (backend format with snake_case)
 */
export interface SequencerGenerateRequest {
  name: string;
  slots: SequencerSlot[];
  artist_separation: number;
  iterations: number;
  stop_mode: 'shortest' | 'all';
  output_playlist?: string;
}

/**
 * Individual track in the generated sequence
 */
export interface SequencerTrack {
  uri: string;
  name: string;
  artists: string[];
  album: string;
  durationMs: number;
}

/**
 * Response with generated track list
 */
export interface SequencerGenerateResponse {
  tracks: SequencerTrack[];
  totalTracks: number;
  executionTimeMs: number;
  auditLog?: unknown[];
}

/**
 * Preview response with estimates and warnings
 */
export interface SequencerPreviewResponse {
  estimatedTracks: number;
  estimatedDuration: number;
  warnings: string[];
}

export const sequencerService = {
  /**
   * Generate sequence from slot configuration
   * @param config - Sequence configuration with slots and settings
   * @returns Generated sequence with tracks
   */
  async generate(config: SequencerGenerateRequest): Promise<SequencerGenerateResponse> {
    return getApiClient().post<SequencerGenerateResponse>('/sequencer/generate', config);
  },

  /**
   * Preview sequence configuration without executing
   * Returns estimated track count, duration, and warnings
   * @param config - Sequence configuration to preview
   * @returns Preview data with estimates
   */
  async preview(config: SequencerGenerateRequest): Promise<SequencerPreviewResponse> {
    return getApiClient().post<SequencerPreviewResponse>('/sequencer/preview', config);
  },
};
